import { LoaderFunctionArgs, RouteObject, useRouteError } from "react-router";
import type { OurRoute } from "./util/Loaders";
import { clientLoader, GLOBAL_ROUTES, Page } from "./util/Loaders";

import Layout from "./routes/__site";

import { GD_ALL } from "./gd";
import {
  loader as AboutLoader,
  meta as AboutMeta,
  default as AboutPage,
} from "./routes/__site/about";
import {
  loader as profLoader,
  meta as profMeta,
  default as ProfPage,
} from "./routes/__site/classes/$classId.$";
import {
  loader as profsLoader,
  meta as profsMeta,
  default as ProfsPage,
} from "./routes/__site/classes/index";
import {
  loader as homeLoader,
  meta as homeMeta,
  default as HomePage,
} from "./routes/__site/index";
import {
  loader as itemLoader,
  meta as itemMeta,
  default as ItemPage,
} from "./routes/__site/items/$itemId.$";
import {
  loader as mapLoader,
  meta as mapMeta,
  default as MapPage,
} from "./routes/__site/map/index";
import {
  loader as npcLoader,
  meta as npcMeta,
  default as NpcPage,
} from "./routes/__site/npcs/$npcId.$";
import {
  loader as npcsLoader,
  meta as npcsMeta,
  default as NpcsPage,
} from "./routes/__site/npcs";
import {
  loader as questLoader,
  meta as questMeta,
  default as QuestPage,
} from "./routes/__site/quests/$questId.$";
import {
  loader as questsLoader,
  meta as questsMeta,
  default as QuestsPage,
} from "./routes/__site/quests/index";
import {
  loader as SkillsLoader,
  meta as SkillsMeta,
  default as SkillsPage,
} from "./routes/__site/skills";
import {
  loader as SkillLoader,
  meta as SkillMeta,
  default as SkillPage,
} from "./routes/__site/skills/$skillId.$";
import { loader as searchLoader } from "./routes/search";
import { loader as itemTooltipLoader } from "./routes/tooltip/item.$id";
import { createRealm, DEFAULT_REALM, realmFromPath } from "./util/Lang";

import "./routes/AUTO_ROUTES";
import { TrpcContext } from "./TrpcServer.server";

export const routes: OurRoute[] = [
  {
    path: "aboutus",
    element: <AboutPage />,
    loader: SRV ? AboutLoader : clientLoader,
    meta: AboutMeta,
  },
  {
    path: "",
    element: <HomePage />,
    loader: SRV ? homeLoader : clientLoader,
    meta: homeMeta,
  },
  {
    path: "items/:itemId/*",
    element: <ItemPage />,
    loader: SRV ? itemLoader : clientLoader,
    meta: itemMeta,
  },
  {
    path: "npcs/:npcId/*",
    element: <NpcPage />,
    loader: SRV ? npcLoader : clientLoader,
    meta: npcMeta,
  },
  {
    path: "npcs",
    element: <NpcsPage />,
    loader: SRV ? npcsLoader : clientLoader,
    meta: npcsMeta,
  },
  {
    path: "quests/:questId/*",
    element: <QuestPage />,
    loader: SRV ? questLoader : clientLoader,
    meta: questMeta,
  },
  {
    path: "quests",
    element: <QuestsPage />,
    loader: SRV ? questsLoader : clientLoader,
    meta: questsMeta,
  },
  {
    path: "classes/:classId/*",
    element: <ProfPage />,
    loader: SRV ? profLoader : clientLoader,
    meta: profMeta,
  },
  {
    path: "classes",
    element: <ProfsPage />,
    loader: SRV ? profsLoader : clientLoader,
    meta: profsMeta,
  },
  {
    path: "map",
    element: <MapPage />,
    loader: SRV ? mapLoader : clientLoader,
    meta: mapMeta,
  },
  {
    path: "skills",
    element: <SkillsPage />,
    loader: SRV ? SkillsLoader : clientLoader,
    meta: SkillsMeta,
  },
  {
    path: "skills/:skillId/*",
    element: <SkillPage />,
    loader: SRV ? SkillLoader : clientLoader,
    meta: SkillMeta,
  },
  {
    path: "tooltip/item",
    element: null,
    loader: SRV ? itemTooltipLoader : clientLoader,
  },
  {
    path: "search",
    element: null,
    loader: SRV ? searchLoader : clientLoader,
  },
  ...GLOBAL_ROUTES,
];

function ShowError() {
  const err = useRouteError() as { data?: { msg?: string } };
  console.log("Page error:", err);
  return (
    <div style={{ fontSize: 20, padding: "20px 0", textAlign: "center" }}>
      {err?.data?.msg ?? "Not found."}
    </div>
  );
}

const pageRoutes: RouteObject[] = routes.map<RouteObject>((r) => {
  return {
    path: r.path !== "" ? r.path : undefined,
    index: r.path === "",
    element: <Page meta={r.meta}>{r.element}</Page>,
    errorElement: <ShowError />,
    loader: (args: LoaderFunctionArgs) => {
      const context = args.context as TrpcContext | undefined;
      const url = new URL(args.request.url);
      const realm = realmFromPath(url.pathname);
      // GD_ALL is null on the client, so the `?.` is required here.
      const gd = GD_ALL?.[realm.game];
      return (
        r.loader?.({
          req: context?.req!, // undefined on client
          res: context?.res!, // undefined on client
          setStatusCode: (code) => context!.res.status(code),
          url,
          realm,
          gd,
          params: args.params,
          searchParams: url.searchParams,
        }) ?? null
      );
    },
    shouldRevalidate: r.shouldRevalidate,

    // Custom fields for l2hub
    meta: r.meta,
  };
});

export const reactRoutes: RouteObject[] = [
  {
    path: "/c1",
    element: <Layout realm={createRealm({ lang: "en", game: "c1" })} />,
    children: pageRoutes,
  },
  {
    path: "/c1-ru",
    element: <Layout realm={createRealm({ lang: "ru", game: "c1" })} />,
    children: pageRoutes,
  },
  {
    path: "/c2",
    element: <Layout realm={createRealm({ lang: "en", game: "c2" })} />,
    children: pageRoutes,
  },
  {
    path: "/c2-ru",
    element: <Layout realm={createRealm({ lang: "ru", game: "c2" })} />,
    children: pageRoutes,
  },
  {
    path: "/c3",
    element: <Layout realm={createRealm({ lang: "en", game: "c3" })} />,
    children: pageRoutes,
  },
  {
    path: "/c3-ru",
    element: <Layout realm={createRealm({ lang: "ru", game: "c3" })} />,
    children: pageRoutes,
  },
  {
    path: "/c4",
    element: <Layout realm={createRealm({ lang: "en", game: "c4" })} />,
    children: pageRoutes,
  },
  {
    path: "/c4-ru",
    element: <Layout realm={createRealm({ lang: "ru", game: "c4" })} />,
    children: pageRoutes,
  },
  {
    path: "/c5",
    element: <Layout realm={createRealm({ lang: "en", game: "c5" })} />,
    children: pageRoutes,
  },
  {
    path: "/c5-ru",
    element: <Layout realm={createRealm({ lang: "ru", game: "c5" })} />,
    children: pageRoutes,
  },
  {
    path: "/il",
    element: <Layout realm={createRealm({ lang: "en", game: "il" })} />,
    children: pageRoutes,
  },
  {
    path: "/il-ru",
    element: <Layout realm={createRealm({ lang: "ru", game: "il" })} />,
    children: pageRoutes,
  },
  {
    path: "/",
    element: <Layout realm={DEFAULT_REALM} />,
    children: pageRoutes,
  },
  {
    path: "/ru",
    element: (
      <Layout realm={createRealm({ lang: "ru", game: DEFAULT_REALM.game })} />
    ),
    children: pageRoutes,
  },
];
